import { useCallback, useEffect, useState } from "react";
import { fetchAgentUsage, type AgentUsage } from "@/services/agentApi";

export type BudgetLevel = "ok" | "warning" | "exhausted";

const WARN_RATIO = 0.8;

/** Whole-percent of the daily budget spent; 0 when no budget is enforced. */
export function budgetPercent(usage: AgentUsage | null): number {
  if (usage === null || usage.budgetTokens <= 0) {
    return 0;
  }
  return Math.floor((usage.usedTokens / usage.budgetTokens) * 100);
}

/** Classifies spend against the daily budget; a budget of 0 means none is enforced. */
export function classifyBudget(usage: AgentUsage | null): BudgetLevel {
  if (usage === null || usage.budgetTokens <= 0) {
    return "ok";
  }
  if (usage.usedTokens >= usage.budgetTokens) {
    return "exhausted";
  }
  return usage.usedTokens >= usage.budgetTokens * WARN_RATIO ? "warning" : "ok";
}

/**
 * The caller's standing against the daily token budget (#678): fetched when the
 * drawer opens and after each turn, so the banner tracks spend as it happens.
 * Fetch failures leave the last known state — the availability probe already
 * covers "the agent is down".
 */
export function useAgentBudget(active: boolean) {
  const [usage, setUsage] = useState<AgentUsage | null>(null);

  const refresh = useCallback(async () => {
    try {
      setUsage(await fetchAgentUsage());
    } catch {
      // Keep the last known state; the next refresh retries.
    }
  }, []);

  useEffect(() => {
    if (active) {
      // Fetch in an effect. Failures keep the last known budget; this is not a query cache.
      // oxlint-disable-next-line react/set-state-in-effect
      void refresh();
    }
  }, [active, refresh]);

  return { usage, level: classifyBudget(usage), refresh } as const;
}
